import { Dispatch, MouseEvent, SetStateAction } from 'react'
import {
  Avatar,
  Button,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import ControlPointIcon from '@mui/icons-material/ControlPoint'
import DeleteIcon from '@mui/icons-material/Delete'

import { HelperWrapper, MoreTableButton, TableRelationIcon } from 'src/components'
import {
  useCancelLibraryImpactRemove,
  useMe,
  useNotification,
  useRemoveLibraryImpacts,
  useTableItemsCheck,
} from 'src/hooks'

import { IImpactLibraryDtoWithId } from 'src/types'
import { TrashTimeoutIcon } from 'src/assets/icons'
import { getTableTimestamp } from 'src/common/utils'
import { useStyles } from './styles'

interface IImpactsLibraryTableProps {
  impacts?: IImpactLibraryDtoWithId[] | void
  sort: string
  setSort: Dispatch<SetStateAction<string>>
}

const ImpactsLibraryTable = ({ impacts, sort, setSort }: IImpactsLibraryTableProps) => {
  const [t] = useTranslation()
  const navigate = useNavigate()
  const { mutateAsync: cancelRemove } = useCancelLibraryImpactRemove()
  const { mutateAsync } = useRemoveLibraryImpacts()
  const { notify } = useNotification()
  const {
    handleAllItemsCheck,
    selectedItemsIds,
    handleItemCheck,
    isItemSelected,
    setSelectedItemsIds,
    isAllUnchecked,
    isAllChecked,
    isIndeterminated,
  } = useTableItemsCheck({
    items: impacts ?? [],
  })
  const { data: user } = useMe()

  const theme = useTheme()
  const classes = useStyles()

  const handleItemsRemove = async () => {
    if (!selectedItemsIds.length) return

    const resp = await mutateAsync(selectedItemsIds)
    if (!resp) return
    notify({
      message: t('items-delete-notification.v'),
      icon: <TrashTimeoutIcon />,
      onButtonClick: async () => {
        await cancelRemove({ ids: selectedItemsIds })
      },
      timeout: 14000,
    })
    setSelectedItemsIds([])
  }

  const handleSortChange = (sortName: string) => () => {
    setSort((prevSort) =>
      prevSort.split('-')[1] === 'asc' ? `${sortName}-desc` : `${sortName}-asc`,
    )
  }

  const handleImpactClick = (id: string) => () => {
    navigate(id)
  }

  const handleInRowClick = (e: any) => {
    e.stopPropagation()
  }

  const handleDuplicateClick = (id: string) => (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    navigate(`add?libraryDuplicate=${id}`)
  }

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow style={{ border: 0 }} sx={{ 'td, th': { border: 0 } }}>
              <TableCell align="center">
                <Checkbox
                  indeterminate={isIndeterminated}
                  sx={{ color: theme.palette.custom.grey2 }}
                  onChange={handleAllItemsCheck}
                  checked={isAllChecked}
                />
              </TableCell>
              <TableCell align="center" />
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('name')}
                  active={sort.split('-')[0] === 'name'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="impact-periods-name-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <HelperWrapper t="un-sdg-title-abbr" />
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('created_at')}
                  active={sort.split('-')[0] === 'created_at'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="table-created-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('updated_at')}
                  active={sort.split('-')[0] === 'updated_at'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="table-modified-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <IconButton disabled={isAllUnchecked} onClick={handleItemsRemove}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {impacts?.map((row) => (
              <TableRow
                onClick={handleImpactClick(row.id)}
                key={row.id}
                style={{
                  borderTop: `1px solid ${theme.palette.custom.grey}`,
                  borderBottom: `1px solid ${theme.palette.custom.grey}`,
                  cursor: 'pointer',
                }}
                sx={{ 'td, th': { border: 0 } }}
              >
                <TableCell>
                  <Checkbox
                    checked={isItemSelected(row.id)}
                    onChange={handleItemCheck(row.id)}
                    sx={{ color: theme.palette.custom.grey2 }}
                    onClick={handleInRowClick}
                  />
                </TableCell>
                <TableCell>
                  {row.image && <Avatar alt={row.name} src={row.image_small || row.image} />}
                </TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>
                  <div className={classes.selectionWrapper}>
                    {row.un_sdg.map((unSdg) => {
                      return (
                        <TableRelationIcon
                          key={unSdg.id}
                          iconAlt={unSdg.name}
                          icon={unSdg.image_small || unSdg.image}
                          title={unSdg.name}
                          isSquared
                        />
                      )
                    })}
                  </div>
                </TableCell>
                <TableCell align="center">
                  {getTableTimestamp({ date: row.created_at, timezone: user?.timezone })}
                </TableCell>
                <TableCell align="center">
                  {getTableTimestamp({ date: row.updated_at, timezone: user?.timezone })}
                </TableCell>
                <TableCell>
                  <MoreTableButton
                    items={[
                      {
                        label: t('duplicate-button-text.v'),
                        onClick: handleDuplicateClick(row.id),
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Button
        sx={{ marginLeft: '135px', marginTop: 5, marginBottom: 5 }}
        color={'primary'}
        size={'large'}
        variant="contained"
        startIcon={<ControlPointIcon />}
        onClick={() => navigate('add')}
      >
        <HelperWrapper t="button-add-new-text" />
      </Button>
    </>
  )
}

export { ImpactsLibraryTable }
